import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Header from "../components/Header";
import { distributorService } from "../services/apiService";
import Toast from "../components/Toast";
import { useProfile } from "../utils/UseProfile";
import { API_BASE_URL, API_PREFIX, API_ENDPOINTS } from "../constants/config";

const API_URL = `${API_BASE_URL}${API_PREFIX}`;
// ─── Inline editable cell ─────────────────────────────────────────────────────
const EditCell = ({ editing, type = "text", name, value, onChange, placeholder, display }) => {
  if (!editing) return <span>{display ?? value ?? "—"}</span>;
  return (
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none" />
  );
};

const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col) return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" /> : <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />;
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th className={`py-2 px-4 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => onSort(col)}>
    {label}<SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

const DistributorBillSearch = () => {
  const { profile } = useProfile();
  const isAdmin = profile?.roleResponse?.role?.includes("ADMIN");
   
  const SS = "dist_bill_search";
  const ss = () => { try { return JSON.parse(sessionStorage.getItem(SS)) || {}; } catch { return {}; } };
  const saveSession = (patch) => { try { sessionStorage.setItem(SS, JSON.stringify({ ...ss(), ...patch })); } catch {} };

  const [searchForm, setSearchForm] = useState(() => ss().searchForm || {
    billNo: "", distributorName: "", mobileNo: "", fromDate: "", toDate: ""
  });
  const [bills, setBills] = useState(() => ss().bills || []);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [hasSearched, setHasSearched] = useState(() => ss().hasSearched || false);
  const [page, setPage] = useState(() => ss().page || 0);
  const PAGE_SIZE = 10;

  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedBills = [...bills].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  useEffect(() => {
    const onUnload = () => sessionStorage.setItem(SS + "_refresh", "1");
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      if (!sessionStorage.getItem(SS + "_refresh")) {
        sessionStorage.removeItem(SS);
      }
      sessionStorage.removeItem(SS + "_refresh");
    };
  }, []);

  const downloadReport = async (type) => {
    try {
      const endpoint = type === "pdf" ? API_ENDPOINTS.DISTRIBUTOR_BILL_REPORT_PDF : API_ENDPOINTS.DISTRIBUTOR_BILL_REPORT_EXCEL;
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Authenticated-User": localStorage.getItem("userName") || "",
        },
        body: JSON.stringify({
          billNumber: searchForm.billNo.trim() || null,
          distributorName: searchForm.distributorName.trim() || null,
          contactNumber: searchForm.mobileNo.trim() || null,
          fromDate: searchForm.fromDate.trim() || null,
          toDate: searchForm.toDate.trim() || null,
          page: 0, size: 1000,
        }),
      });
      if (!res.ok) throw new Error("Failed to generate report");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bill_report_${new Date().toISOString().slice(0,10)}.${type === "pdf" ? "pdf" : "xlsx"}`;
      link.click();
    } catch { showMessage("error", "Failed to download report"); }
  };

  const showMessage = (type, text, identifier = null) => {
    setToast({ type, identifier, message: text });
  };

  // ─── Inline edit ──────────────────────────────────────────────────────────
  const startEdit = (bill) => {
    setEditMap(prev => ({
      ...prev,
      [bill.billId]: {
        billNumber:    bill.billNumber || "",
        billAmount:    bill.billAmount ?? "",
        paidAmount:    bill.paidAmount ?? "",
        paymentStatus: bill.paymentStatus || "",
        billDate:      bill.billDate ? new Date(bill.billDate).toISOString().split("T")[0] : "",
        transactionId: bill.transactionId || "",
      },
    }));
  };

  const cancelEdit = (billId) => {
    setEditMap(prev => { const n = { ...prev }; delete n[billId]; return n; });
  };

  const handleEditChange = (billId, e) => {
    const { name, value } = e.target;
    setEditMap(prev => ({ ...prev, [billId]: { ...prev[billId], [name]: value } }));
  };

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill "${bill.billNumber}"? This cannot be undone.`)) return;
    setDeletingId(bill.billId);
    setOpenMenuId(null);
    try {
      await distributorService.deleteBill(bill.billId, bill.distributorId);
      setBills(prev => prev.filter(b => b.billId !== bill.billId));
      showMessage("success", "Bill deleted successfully", `Bill No: ${bill.billNumber}`);
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Delete failed", `Bill No: ${bill.billNumber}`);
    } finally { setDeletingId(null); }
  };

  const handleSave = async (bill) => {
    const data = editMap[bill.billId];
    setSavingId(bill.billId);
    try {
      await distributorService.updateBill({
        billId:        bill.billId,
        distributorId: bill.distributorId,
        billNumber:    data.billNumber,
        billAmount:    parseFloat(data.billAmount),
        paidAmount:    parseFloat(data.paidAmount) || 0,
        paymentStatus: data.paymentStatus,
        billDate:      new Date(data.billDate).toISOString(),
        transactionId: data.transactionId || null,
      });
      setBills(prev => prev.map(b => b.billId === bill.billId
        ? { ...b, ...data, billAmount: parseFloat(data.billAmount), paidAmount: parseFloat(data.paidAmount) || 0, billDate: new Date(data.billDate).toISOString() }
        : b
      ));
      cancelEdit(bill.billId);
      showMessage("success", "Bill updated successfully", `Bill No: ${data.billNumber}`);
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Update failed", `Bill No: ${data.billNumber}`);
    } finally { setSavingId(null); }
  };

  // ─── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e.preventDefault();
    const hasSearchCriteria = Object.values(searchForm).some(val => val.trim());
    if (!hasSearchCriteria) { showMessage("error", "Please enter at least one search criteria"); return; }

    setLoading(true);
    setHasSearched(true);
    saveSession({ searchForm, hasSearched: true });
    try {
      const data = await distributorService.searchBills({
        billNumber:      searchForm.billNo.trim(),
        distributorName: searchForm.distributorName.trim(),
        contactNumber:   searchForm.mobileNo.trim(),
        fromDate: searchForm.fromDate.trim(),
        toDate: searchForm.toDate.trim(),
        page: 0, size: 50,
      });
      const list = data.content ?? [];
      setBills(list);
      setPage(0);
      saveSession({ bills: list, page: 0 });
      showMessage("success", `Found ${list.length} bill(s)`,
        searchForm.billNo.trim() ? `Bill No: ${searchForm.billNo.trim()}`
        : searchForm.distributorName.trim() ? `Distributor: ${searchForm.distributorName.trim()}` : null);
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Failed to search bills");
    } finally { setLoading(false); }
  };

  const handleReset = () => {
    setSearchForm({ billNo: "", distributorName: "", mobileNo: "", fromDate: "", toDate: "" });
    setBills([]); setToast(null); setHasSearched(false); setPage(0);
    sessionStorage.removeItem(SS);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} identifier={toast.identifier} message={toast.message} onClose={() => setToast(null)} />}

      {/* Search filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Distributor Bills
        </h2>
        <div className="p-2">
          <form onSubmit={handleSearch}>
            <div className="flex flex-wrap gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bill Number</label>
                <input type="text" value={searchForm.billNo} placeholder="e.g. BILL-001"
                  onChange={(e) => { setSearchForm(prev => { const u = { ...prev, billNo: e.target.value }; saveSession({ searchForm: u }); return u; }); }}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Distributor Name</label>
                <input type="text" value={searchForm.distributorName} placeholder="e.g. ABC Pharma"
                  onChange={(e) => { setSearchForm(prev => { const u = { ...prev, distributorName: e.target.value }; saveSession({ searchForm: u }); return u; }); }}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number</label>
                <input type="tel" value={searchForm.mobileNo} placeholder="e.g. 9876543210" maxLength="10"
                  onChange={(e) => { const value = e.target.value.replace(/\D/g, '').slice(0, 10); setSearchForm(prev => { const u = { ...prev, mobileNo: value }; saveSession({ searchForm: u }); return u; }); }}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                <input type="date" value={searchForm.fromDate}
                  onChange={(e) => { setSearchForm(prev => { const u = { ...prev, fromDate: e.target.value }; saveSession({ searchForm: u }); return u; }); }}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                <input type="date" value={searchForm.toDate}
                  onChange={(e) => { setSearchForm(prev => { const u = { ...prev, toDate: e.target.value }; saveSession({ searchForm: u }); return u; }); }}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
                <i className="fas fa-redo" /> Reset
              </button>
              <button type="submit" disabled={loading}
                className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                {loading ? <><i className="fas fa-spinner fa-spin" />Searching...</> : <><i className="fas fa-search" />Search</>}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-4 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 Distributor Bill Details
            {bills.length > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({bills.length} bill{bills.length !== 1 ? "s" : ""})</span>}
          </h2>
          {bills.length > 0 && (
            <div className="flex gap-2">
              <button onClick={() => downloadReport("pdf")} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow transition">
                <i className="fas fa-file-pdf" /> PDF
              </button>
              <button onClick={() => downloadReport("excel")} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold shadow transition">
                <i className="fas fa-file-excel" /> Excel
              </button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800 table-auto">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide">
              <tr>
                <th className="py-2 px-4 text-left">#</th>
                <Th col="billNumber" label="Bill No" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="distributorName" label="Distributor" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="contactNumber" label="Mobile No" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="billAmount" label="Bill Amount" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="paidAmount" label="Paid Amount" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <th className="py-2 px-4 text-right">Rest Amount</th>
                <Th col="paymentStatus" label="Transaction Type" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="billDate" label="Bill Date" sortConfig={sortConfig} onSort={handleSort} />
                {isAdmin && <th className="py-2 px-4 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {!hasSearched ? (
                <tr><td colSpan={isAdmin ? 10 : 9} className="py-10 text-center text-gray-400">
                  <i className="fas fa-search text-4xl mb-3 block" />
                  <p className="text-sm">Use the search above to find bills.</p>
                </td></tr>
              ) : bills.length === 0 ? (
                <tr><td colSpan={isAdmin ? 10 : 9} className="py-10 text-center text-gray-400">
                  <i className="fas fa-inbox text-4xl mb-3 block" />
                  <p className="text-sm">No bills found. Try different search criteria.</p>
                </td></tr>
              ) : (
                sortedBills.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((bill, idx) => {
                  const editing = !!editMap[bill.billId];
                  const ed = editMap[bill.billId] || {};
                  const restAmt = editing
                    ? (parseFloat(ed.billAmount) || 0) - (parseFloat(ed.paidAmount) || 0)
                    : Number(bill.billAmount) - Number(bill.paidAmount);
                  return (
                    <tr key={bill.billId} className={`border-t hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"}`}>
                      <td className="py-1 px-4 text-gray-500">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="py-1 px-4 font-medium text-indigo-700">
                        <EditCell editing={editing} name="billNumber" value={ed.billNumber}
                          onChange={(e) => handleEditChange(bill.billId, e)} display={bill.billNumber} />
                      </td>
                      <td className="py-1 px-4">{bill.distributorName || bill.distributorId}</td>
                      <td className="py-1 px-4">{bill.contactNumber || "—"}</td>
                      <td className="py-1 px-4 text-right font-medium">
                        {editing
                          ? <input type="number" name="billAmount" value={ed.billAmount} step="0.01" min="0"
                              onChange={(e) => handleEditChange(bill.billId, e)}
                              className="w-24 px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none text-right" />
                          : `₹${Number(bill.billAmount).toFixed(2)}`}
                      </td>
                      <td className="py-1 px-4 text-right font-medium">
                        {editing
                          ? <input type="number" name="paidAmount" value={ed.paidAmount} step="0.01" min="0"
                              onChange={(e) => handleEditChange(bill.billId, e)}
                              className="w-24 px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none text-right" />
                          : `₹${Number(bill.paidAmount).toFixed(2)}`}
                      </td>
                      <td className="py-1 px-4 text-right font-medium">
                        <span className={restAmt > 0 ? "text-red-600" : "text-green-600"}>
                          ₹{restAmt.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-1 px-4">
                        {editing ? (
                          <select name="paymentStatus" value={ed.paymentStatus}
                            onChange={(e) => handleEditChange(bill.billId, e)}
                            className="px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none">
                            <option value="CASH">CASH</option>
                            <option value="UPI">UPI</option>
                            <option value="Card">CARD</option>
                            <option value="BankTransfer">Bank Transfer</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            bill.paymentStatus === "PAID" ? "bg-green-100 text-green-700"
                            : bill.paymentStatus === "PARTIAL" ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                          }`}>{bill.paymentStatus}</span>
                        )}
                      </td>
                      <td className="py-1 px-4">
                        {editing
                          ? <input type="date" name="billDate" value={ed.billDate}
                              onChange={(e) => handleEditChange(bill.billId, e)}
                              className="px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none" />
                          : new Date(bill.billDate).toLocaleDateString()}
                      </td>
                      {isAdmin && (
                        <td className="py-1 px-4 text-center">
                          {editing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => handleSave(bill)} disabled={savingId === bill.billId}
                                title="Save" className="text-green-600 hover:text-green-800 disabled:opacity-50">
                                {savingId === bill.billId ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                              </button>
                              <button onClick={() => cancelEdit(bill.billId)} title="Cancel" className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-times" />
                              </button>
                            </div>
                          ) : (
                            <div className="inline-block">
                              <button
                                onClick={(e) => {
                                  if (openMenuId === bill.billId) { setOpenMenuId(null); return; }
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX - 128 });
                                  setOpenMenuId(bill.billId);
                                }}
                                disabled={deletingId === bill.billId}
                                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-800 disabled:opacity-50"
                                title="Actions">
                                {deletingId === bill.billId ? <i className="fas fa-spinner fa-spin text-xs" /> : <i className="fas fa-ellipsis-v text-xs" />}
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {bills.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, bills.length)} of {bills.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: Math.ceil(bills.length / PAGE_SIZE) }, (_, i) => (
                <button key={i} onClick={() => setPage(i)}
                  className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(bills.length / PAGE_SIZE) - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setPage(Math.ceil(bills.length / PAGE_SIZE) - 1)} disabled={page >= Math.ceil(bills.length / PAGE_SIZE) - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>

      {openMenuId && ReactDOM.createPortal(
        <div ref={menuRef} style={{ position: "absolute", top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          {(() => {
            const bill = bills.find(b => b.billId === openMenuId);
            if (!bill) return null;
            return (
              <>
                <button onClick={() => { startEdit(bill); setOpenMenuId(null); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2">
                  <i className="fas fa-edit w-3" /> Edit
                </button>
                <button onClick={() => handleDelete(bill)}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
                  <i className="fas fa-trash w-3" /> Delete
                </button>

              </>
            );
          })()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DistributorBillSearch;
